/**
 * People (Сотрудники) — company directory.
 *
 * Tamagui port of the handoff `PeopleScreen` + `PersonDrawer`:
 *  • search by name + grid/list view toggle; `?department=<имя>` (переход с плитки
 *    оргструктуры) сужает каталог до одного подразделения — снимается крестиком на чипе
 *  • grid of person cards (avatar, role, office, message/call actions)
 *  • list rows (avatar, name/role, dept, office)
 *  • avatar corner badges: palm when on vacation, gift (+ card confetti) on a birthday
 *  • click a person → right-hand profile drawer (contact details + status)
 *
 * Renders the live `/users` directory only; an empty or unreachable response
 * shows the empty state (no demo fallback).
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { FlatList } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { Input, Stack, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../src/i18n'
import { fmtDate } from '../../src/lib/format'
import { useResponsive } from '../../src/lib/responsive'
import { PersonDrawer, liveVM, type PersonVM } from '../../src/features/users/PersonDrawer'
import { useUsers } from '../../src/features/users/useUsers'
import { palette } from '../../src/theme/palette'
import { tone } from '../../src/theme/tones'
import { Avatar, Card, Confetti, Enter, Icon, IconButton, QueryState } from '../../src/ui'
import { Page } from '../../src/ui/Screen'

// Virtualized grid/list — RN's FlatList, not @shopify/flash-list: verified
// live in the web preview (fetch mocked to 60 users) that FlashList mounts
// ALL rows in both views on react-native-web — no windowing (0.19.13, RNW's
// ScrollView doesn't drive FlashList's RecyclerListView recycling the way it
// expects). FlatList windows correctly there. Only the first ENTER_LIMIT
// cells get the staggered Enter mount animation (a setTimeout each) —
// hundreds of rows would otherwise spin up hundreds of timers on mount for a
// cascade nobody sees past the fold.
const ENTER_LIMIT = 12
const GRID_GAP = 16
const GRID_CARD_TARGET = 200 // px — grid numColumns = floor(measured width / this); колонка тянется на всю долю

// ── Mini tooltip ─────────────────────────────────────────────────────────────
// Local, portal-free hint: inverse pill on hover (web) or tap (touch). stopPropagation
// keeps a tap on the badge from opening the person card underneath.
function Hint({ label, side = 'top', children }: { label: string; side?: 'top' | 'right'; children: ReactNode }) {
  const [visible, setVisible] = useState(false)
  return (
    <Stack
      position="relative"
      accessibilityLabel={label}
      onHoverIn={() => setVisible(true)}
      onHoverOut={() => setVisible(false)}
      onPress={(e) => {
        e.stopPropagation()
        setVisible((v) => !v)
      }}
    >
      {children}
      {visible && (
        <Stack
          position="absolute"
          zIndex={10}
          {...(side === 'top'
            ? { bottom: '100%', marginBottom: 6, left: -4 }
            : { left: '100%', marginLeft: 6, top: -2 })}
        >
          <Stack backgroundColor="$color" borderRadius={5} paddingHorizontal={8} paddingVertical={4}>
            <Text fontSize={11} fontWeight="600" color="$background" numberOfLines={1}>
              {label}
            </Text>
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

// ── Avatar corner badge ──────────────────────────────────────────────────────
// Bordered icon disc that overlays the avatar (vacation / birthday markers).
function AvatarBadge({ icon, bg, size = 18 }: { icon: string; bg: string; size?: number }) {
  return (
    <Stack
      width={size}
      height={size}
      borderRadius={999}
      backgroundColor={bg}
      borderWidth={2}
      borderColor="$background"
      alignItems="center"
      justifyContent="center"
    >
      <Icon name={icon} size={Math.round(size * 0.55)} color="#fff" />
    </Stack>
  )
}

// Подпись бейджа отпуска: «В отпуске до …» + «· Замещает: …», если есть замещающий.
function vacationLabel(p: PersonVM, l: (key: string) => string) {
  const base = p.vacationUntil ? l('people.onVacationUntil').replace('{date}', fmtDate(p.vacationUntil)) : l('people.onVacation')
  return p.deputyName ? `${base} · ${l('people.deputy').replace('{name}', p.deputyName)}` : base
}

// ── Grid card ──────────────────────────────────────────────────────────────────
// Размер карточки задаёт ячейка сетки: она — колонка, там flexBasis лёг бы в высоту
// (карточки распирало до 208px пустоты), а ширина шла от minWidth — отсюда рваные колонки.
function PersonCard({ p, onOpen }: { p: PersonVM; onOpen: (p: PersonVM) => void }) {
  const { t, l } = useApp()
  const vacLabel = vacationLabel(p, l)
  return (
    <Card
      onPress={() => onOpen(p)}
      cursor="pointer"
      animation="quick"
      width="100%"
      flex={1}
      padding={16}
      alignItems="center"
      gap={10}
      hoverStyle={{ borderColor: '$primary', y: -2, shadowOpacity: 0.2, shadowRadius: 12 }}
      pressStyle={{ y: 0, scale: 0.985 }}
    >
      {p.birthday && <Confetti count={10} />}
      <Stack position="relative">
        {/* пальма заменяет presence-точку в отпуске */}
        <Avatar name={t(p.name)} seed={p.id} size="lg" status={p.vacation ? undefined : p.status} />
        {/* p.vacation придёт, только когда бэк начнёт отдавать onVacation — иначе бейдж не рендерится */}
        {p.vacation && (
          <Stack position="absolute" bottom={-1} right={-1}>
            <Hint label={vacLabel}>
              <AvatarBadge icon="palmtree" bg={tone('green').solid} />
            </Hint>
          </Stack>
        )}
        {p.birthday && (
          <Stack position="absolute" top={-2} right={-2}>
            <Hint label={l('people.birthdayToday')}>
              <AvatarBadge icon="gift" bg={tone('gold').solid} />
            </Hint>
          </Stack>
        )}
      </Stack>
      <YStack alignItems="center" gap={3}>
        <Text fontSize={14} fontWeight="700" color="$color" textAlign="center">
          {t(p.name)}
        </Text>
        <Text fontSize={11} color="$muted" textAlign="center" lineHeight={15} numberOfLines={2}>
          {t(p.role)}
        </Text>
      </YStack>
      {!!t(p.office) && (
        <XStack alignItems="center" gap={5}>
          <Icon name="map-pin" size={12} color="$tertiary" />
          <Text fontSize={11} color="$muted">
            {t(p.office)}
          </Text>
        </XStack>
      )}
    </Card>
  )
}

// ── List row ──────────────────────────────────────────────────────────────────
function PersonRow({ p, first, onOpen }: { p: PersonVM; first: boolean; onOpen: (p: PersonVM) => void }) {
  const { t, l } = useApp()
  const { isPhone } = useResponsive()
  const vacLabel = vacationLabel(p, l)
  return (
    <XStack
      onPress={() => onOpen(p)}
      cursor="pointer"
      animation="quick"
      alignItems="center"
      gap={12}
      paddingHorizontal={16}
      paddingVertical={12}
      borderTopWidth={first ? 0 : 1}
      borderColor="$borderSecondary"
      hoverStyle={{ backgroundColor: '$backgroundHover' }}
    >
      <Stack position="relative">
        {/* пальма заменяет presence-точку в отпуске */}
        <Avatar name={t(p.name)} seed={p.id} size="sm" status={p.vacation ? undefined : p.status} />
        {/* p.vacation придёт, только когда бэк начнёт отдавать onVacation — иначе бейдж не рендерится */}
        {p.vacation && (
          <Stack position="absolute" bottom={-1} right={-1}>
            <Hint label={vacLabel} side="right">
              <AvatarBadge icon="palmtree" bg={tone('green').solid} size={14} />
            </Hint>
          </Stack>
        )}
        {p.birthday && (
          <Stack position="absolute" top={-2} right={-2}>
            <Hint label={l('people.birthdayToday')} side="right">
              <AvatarBadge icon="gift" bg={tone('gold').solid} size={14} />
            </Hint>
          </Stack>
        )}
      </Stack>
      <YStack flex={1} minWidth={0}>
        <Text fontSize={13} fontWeight="600" color="$color" numberOfLines={1}>
          {t(p.name)}
        </Text>
        <Text fontSize={11} color="$muted" numberOfLines={1}>
          {t(p.role)}
        </Text>
      </YStack>
      {!isPhone && (
        <Text width={150} fontSize={12} color="$muted" numberOfLines={1}>
          {t(p.deptLabel)}
        </Text>
      )}
      {!isPhone && !!t(p.office) && (
        <XStack width={130} alignItems="center" gap={5}>
          <Icon name="map-pin" size={12} color="$tertiary" />
          <Text fontSize={12} color="$muted" numberOfLines={1}>
            {t(p.office)}
          </Text>
        </XStack>
      )}
      <IconButton onPress={(e) => e.stopPropagation()}>
        <Icon name="message-square" size={15} color="$muted" />
      </IconButton>
    </XStack>
  )
}

// ── View toggle ──────────────────────────────────────────────────────────────────
function ViewToggle({ view, onChange }: { view: 'grid' | 'list'; onChange: (v: 'grid' | 'list') => void }) {
  const opt = (key: 'grid' | 'list', icon: string) => {
    const on = view === key
    return (
      <XStack
        onPress={() => onChange(key)}
        cursor="pointer"
        animation="quick"
        width={34}
        height={30}
        alignItems="center"
        justifyContent="center"
        borderRadius={5}
        backgroundColor={on ? '$background' : 'transparent'}
        {...(on ? { shadowColor: palette.shadow, shadowOpacity: 0.12, shadowRadius: 6, shadowOffset: { width: 0, height: 1 } } : null)}
      >
        <Icon name={icon} size={15} color={on ? '$primary' : '$muted'} />
      </XStack>
    )
  }
  return (
    <XStack gap={4} backgroundColor="$bgLayout" padding={4} borderRadius={7}>
      {opt('grid', 'layout-grid')}
      {opt('list', 'list')}
    </XStack>
  )
}

// ── Virtualized grid ────────────────────────────────────────────────────────────
// FlatList has no CSS-style auto-fill — numColumns is a fixed integer, so the
// column count is derived from the measured container width instead.
function PeopleGrid({ data, onOpen }: { data: PersonVM[]; onOpen: (p: PersonVM) => void }) {
  const [width, setWidth] = useState(0)
  const numColumns = Math.max(1, Math.floor((width + GRID_GAP) / (GRID_CARD_TARGET + GRID_GAP)))

  // Доля колонки, а не auto: иначе ячейка меряется по содержимому и колонки в
  // соседних рядах разъезжаются, а справа остаётся пустая полоса.
  const renderItem = useCallback(
    ({ item, index }: { item: PersonVM; index: number }) => {
      const cell = { width: `${100 / numColumns}%` as const, padding: GRID_GAP / 2 }
      const card = <PersonCard p={item} onOpen={onOpen} />
      return index < ENTER_LIMIT ? (
        <Enter index={index} {...cell}>
          {card}
        </Enter>
      ) : (
        <Stack {...cell}>{card}</Stack>
      )
    },
    [onOpen, numColumns],
  )

  return (
    <Stack flex={1} onLayout={(e) => setWidth(e.nativeEvent.layout.width)}>
      {/* width starts at 0 before the first layout pass — numColumns would be
          bogus, so skip the first render rather than mount-then-remount. */}
      {width > 0 && (
        <FlatList key={`grid-${numColumns}`} data={data} numColumns={numColumns} keyExtractor={(p) => p.id} renderItem={renderItem} />
      )}
    </Stack>
  )
}

// ── Screen ────────────────────────────────────────────────────────────────────────
export default function PeopleScreen() {
  const { t, l } = useApp()
  const [q, setQ] = useState('')
  const [view, setView] = useState<'grid' | 'list'>('grid')
  const [sel, setSel] = useState<PersonVM | null>(null)

  const router = useRouter()
  // Приходит с плитки «Оргструктура → Подразделения»: сырое название, как в /users/departments.
  const { department } = useLocalSearchParams<{ department?: string }>()

  const usersQuery = useUsers()
  const all = useMemo<PersonVM[]>(() => (usersQuery.data ?? []).map(liveVM), [usersQuery.data])

  const filtered = useMemo(
    () =>
      all.filter(
        (p) =>
          t(p.name).toLowerCase().includes(q.trim().toLowerCase()) &&
          (!department || p.deptLabel === department),
      ),
    [all, department, q, t],
  )

  const renderRow = useCallback(({ item, index }: { item: PersonVM; index: number }) => {
    const row = <PersonRow p={item} first={index === 0} onOpen={setSel} />
    return index < ENTER_LIMIT ? (
      <Enter index={index} step={30} from={8}>
        {row}
      </Enter>
    ) : (
      row
    )
  }, [])

  return (
    <>
      {/* scroll=false: the grid/list below owns scrolling — FlatList can't
          virtualize while nested inside Page's own ScrollView. */}
      <Page title={l('people.title')} subtitle={l('people.subtitle')} scroll={false}>
        {/* search + view toggle */}
        <XStack alignItems="center" gap={12} flexWrap="wrap">
          <XStack
            flex={1}
            minWidth={200}
            maxWidth={340}
            alignItems="center"
            gap={8}
            height={38}
            paddingHorizontal={11}
            borderWidth={1}
            borderColor="$borderColor"
            borderRadius={7}
            backgroundColor="$background"
          >
            <Icon name="search" size={15} color="$tertiary" />
            <Input
              unstyled
              flex={1}
              height={36}
              fontSize={14}
              color="$color"
              borderWidth={0}
              backgroundColor="transparent"
              placeholder={l('people.searchPlaceholder')}
              placeholderTextColor="$tertiary"
              value={q}
              onChangeText={setQ}
            />
          </XStack>
          {!!department && (
            <XStack
              onPress={() => router.setParams({ department: '' })}
              cursor="pointer"
              animation="quick"
              hoverStyle={{ opacity: 0.85 }}
              alignItems="center"
              gap={6}
              height={38}
              paddingHorizontal={11}
              borderWidth={1}
              borderColor="$borderColor"
              borderRadius={999}
              backgroundColor="$bgLayout"
            >
              <Text fontSize={13} fontWeight="600" color="$color">
                {t(department)}
              </Text>
              <Icon name="x" size={13} color="$muted" />
            </XStack>
          )}
          <Stack flex={1} />
          <ViewToggle view={view} onChange={setView} />
        </XStack>

        <QueryState q={usersQuery}>
          <Text fontSize={11} color="$muted">
            {filtered.length} {l('people.count')}
          </Text>

          {filtered.length === 0 ? (
            <Card padding={48} alignItems="center">
              <Text fontSize={13} color="$muted">
                {l('people.empty')}
              </Text>
            </Card>
          ) : view === 'grid' ? (
            <PeopleGrid data={filtered} onOpen={setSel} />
          ) : (
            <Card flex={1} overflow="hidden">
              <FlatList data={filtered} keyExtractor={(p) => p.id} renderItem={renderRow} />
            </Card>
          )}
        </QueryState>
      </Page>

      <PersonDrawer p={sel} onClose={() => setSel(null)} />
    </>
  )
}
